import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LayoutDashboard, Hotel, Plane, Users, Bus, FileText, Package, Download, UserCog, ShoppingCart,
  Activity, AlertCircle, CheckCircle, XCircle, Settings, Star, DollarSign, Eye
} from 'lucide-react';
import HotelsManagement from '../components/admin/HotelsManagement';
import AirlinesManagement from '../components/admin/AirlinesManagement';
import MuthowifsManagement from '../components/admin/MuthowifsManagement';
import PackageTypesManagement from '../components/admin/PackageTypesManagement';
import TransportManagement from '../components/admin/TransportManagement';
import VisasManagement from '../components/admin/VisasManagement';
import PackagesManagement from '../components/admin/PackagesManagement';
import OrdersManagement from '../components/admin/OrdersManagement';
import SettingsManagement from '../components/admin/SettingsManagement';
import UsersManagement from '../components/admin/UsersManagement';
import JamaahManagement from '../components/admin/JamaahManagement';
import KuotaSyncManagement from '../components/admin/KuotaSyncManagement';
import TestimonialsManagement from '../components/admin/TestimonialsManagement';
import AffiliateManagement from '../components/admin/AffiliateManagement';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get('tab') || 'overview'
  );
  const [viewOrderId, setViewOrderId] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-muted-foreground">Memuat...</p>
      </div>
    );
  }

  if (!user || !['admin', 'super_admin'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  const menuItems = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'hotels', label: 'Hotels', icon: Hotel },
    { id: 'airlines', label: 'Airlines', icon: Plane },
    { id: 'muthowifs', label: 'Muthowif', icon: UserCog },
    { id: 'transport', label: 'Transport', icon: Bus },
    { id: 'visas', label: 'Visa', icon: FileText },
    { id: 'packages', label: 'Paket', icon: Package },
    { id: 'package-types', label: 'Tipe Paket', icon: Package },
    { id: 'kuota', label: 'Tarik Data', icon: Download },
    { id: 'orders', label: 'Pesanan', icon: ShoppingCart },
    { id: 'jamaah', label: 'Jamaah', icon: Users },
    { id: 'settings', label: 'Pengaturan', icon: Settings },
    { id: 'users', label: 'User', icon: Users },
    { id: 'testimonials', label: 'Testimoni', icon: Star },
    { id: 'affiliate', label: 'Affiliate', icon: DollarSign },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab onViewOrder={(id) => { setViewOrderId(id); setActiveTab('orders'); }} />;
      case 'hotels': return <HotelsManagement />;
      case 'airlines': return <AirlinesManagement />;
      case 'muthowifs': return <MuthowifsManagement />;
      case 'transport': return <TransportManagement />;
      case 'visas': return <VisasManagement />;
      case 'packages': return <PackagesManagement />;
      case 'package-types': return <PackageTypesManagement />;
      case 'kuota': return <KuotaSyncManagement />;
      case 'orders': return <OrdersManagement viewOrderId={viewOrderId} onViewed={() => setViewOrderId(null)} />;
      case 'jamaah': return <JamaahManagement />;
      case 'settings': return <SettingsManagement />;
      case 'users': return <UsersManagement />;
      case 'testimonials': return <TestimonialsManagement />;
      case 'affiliate': return <AffiliateManagement />;
      default: return <OverviewTab onViewOrder={(id) => { setViewOrderId(id); setActiveTab('orders'); }} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="flex">
        <aside className="w-64 min-h-screen bg-card border-r border-border">
          <div className="p-6">
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">Admin Panel</h2>
            <nav className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant={activeTab === item.id ? 'default' : 'ghost'}
                    className="w-full justify-start"
                    onClick={() => setActiveTab(item.id)}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Button>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="flex-1 p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

const OverviewTab = ({ onViewOrder }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatRupiah = (num) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);
  };

  const formatDate = (d) => {
    return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/stats`);
        setStats(res.data);
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Memuat statistik...</div>;
  }

  const cards = [
    { label: 'Total Order', value: stats?.total_orders || 0, icon: ShoppingCart, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Total Pendapatan', value: formatRupiah(stats?.total_revenue), icon: DollarSign, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Total Pengguna', value: stats?.total_users || 0, icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Total Paket', value: stats?.total_packages || 0, icon: Package, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { label: 'Total Jamaah', value: stats?.total_jamaah || 0, icon: Activity, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
    { label: 'Order Tertunda', value: stats?.pending_orders || 0, icon: AlertCircle, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { label: 'Order Lunas', value: stats?.paid_orders || 0, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Order Gagal', value: stats?.failed_orders || 0, icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
  ];

  const statusColor = (status) => {
    switch (status) {
      case 'paid': return 'default';
      case 'pending': return 'secondary';
      case 'failed': return 'destructive';
      default: return 'secondary';
    }
  };

  const statusLabel = (status) => {
    const m = { paid: 'Lunas', pending: 'Pending', failed: 'Gagal', waiting_confirmation: 'Menunggu Konfirmasi', cancelled: 'Dibatalkan' };
    return m[status] || status;
  };

  return (
    <div>
      <h1 className="text-3xl font-bold font-['Outfit'] mb-6">Dashboard Penjualan</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className="rounded-2xl overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{card.label}</p>
                    <p className="text-2xl font-bold">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${card.bg}`}>
                    <Icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Pesanan Terbaru</CardTitle>
        </CardHeader>
        <CardContent>
          {(!stats?.recent_orders || stats.recent_orders.length === 0) ? (
            <p className="text-center text-muted-foreground py-8">Belum ada pesanan</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold text-sm">Order #</th>
                    <th className="text-left p-3 font-semibold text-sm">Tanggal</th>
                    <th className="text-left p-3 font-semibold text-sm">Total</th>
                    <th className="text-left p-3 font-semibold text-sm">Status</th>
                    <th className="text-right p-3 font-semibold text-sm">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-muted/50">
                      <td className="p-3 font-medium text-sm">{order.order_number}</td>
                      <td className="p-3 text-sm text-muted-foreground">{formatDate(order.created_at)}</td>
                      <td className="p-3 text-sm">{formatRupiah(order.total_amount)}</td>
                      <td className="p-3">
                        <Badge variant={statusColor(order.payment_status)}>{statusLabel(order.payment_status)}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onViewOrder(order.id); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
